import { useEffect } from "react";
import ErrorComponent from "../../components/ErrorComponent";
import CountryDetail from "../../components/CountryDetail/CountryDetail";
import type { GetStaticPaths, GetStaticProps } from "next";
import { CACHE_REVALIDATE } from "../../utils/constants";
import fetchUtil from "../../utils/fetchUtil";
import { MetaTagsComp } from "../../components/MetaTagsComp";
import Countrypage from "../../components/Countrypage";

const CountryPage = (props: any) => {
  if (!props.pageData || props.pageData.length === 0) {
    return <ErrorComponent />;
  }

  const { meta } = props.pageData[0] || {};
  const slug = props.pageData[0].slug;

  const updatedMeta = meta
    ? {
        ...meta,
        canonicalUrl: `${process.env.NEXT_PUBLIC_BASE_URL}/country/${slug}`,
      }
    : null;

  useEffect(() => {
    const customStyle = document.documentElement.style;
    customStyle.setProperty("--navbar-color", "#6C48F0");
    customStyle.setProperty("--navbar-menu-color", "#ffffff");
  }, []);

  return (
    <>
      {updatedMeta && props.pageData?.length > 0 && <MetaTagsComp {...updatedMeta} />}
      {props.mode === "newUI" ? <CountryDetail {...props} /> : <Countrypage {...props} />}
    </>
  );
};

export default CountryPage;

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { slug } = context.params as { slug: string };

  function getCountry(countrySlug: string) {
    return countrySlug.replace("study-in-", "");
  }

  // Wrap CMS calls so a failed/forbidden response (e.g. 403) falls back to a
  // safe default instead of throwing and breaking the whole page.
  const safeFetch = async <T,>(url: string, fallback: T): Promise<T> => {
    try {
      return (await fetchUtil(url, "GET", {}, "", false)) as T;
    } catch (error) {
      return fallback;
    }
  };

  try {
    const [pageData, pageDataNewUI, scholarshipsData] = await Promise.all([
      safeFetch<any>(`${process.env.NEXT_PUBLIC_API_BASE_URL}/study-countries?slug=${slug}`, []),
      safeFetch<any>(`${process.env.NEXT_PUBLIC_API_BASE_URL}/study-destination-pages?slug=${slug}`, []),
      safeFetch<any>(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/new-country-specific-scholarships?slug=${getCountry(slug)}`,
        [],
      ),
    ]);

    const newUIEnabled = pageData?.[0]?.showNewUI ?? false;
    const countryName = pageData?.[0]?.name ?? "";
    const blogsData = countryName
      ? await safeFetch<any>(
          `${process.env.NEXT_PUBLIC_API_BASE_URL}/blogs?_sort=date:DESC&stu_dest_categories.category_contains=${countryName}&stu_dest_categories.category_ncontains=News&_limit=10`,
          [],
        )
      : [];

    if (newUIEnabled) {
      if (!pageDataNewUI?.length) {
        return { notFound: true, revalidate: 60 };
      }

      return {
        props: { pageData: pageDataNewUI, scholarshipsData, blogsData, mode: "newUI" },
        revalidate: CACHE_REVALIDATE,
      };
    }

    if (!pageData?.length) {
      return { notFound: true, revalidate: 60 };
    }

    return {
      props: { pageData, scholarshipsData, blogsData, mode: "oldUI" },
      revalidate: CACHE_REVALIDATE,
    };
  } catch (err) {
    // Return 404 with a short revalidate so a real page recovers within ~1 min instead of being frozen for the full window.
    return { notFound: true, revalidate: 60 };
  }
};
